// useDriveApi.js

import { useCallback, useEffect, useRef } from 'react';
import google from '../constants/google';

const FOLDER_MIME = google.FOLDER_MIME;
const CACHE_TTL_MS = google.CACHE_TTL_MS;

export default function useDriveApi({
    tree, setTree, folderId, setFolderId, expanded, setExpanded, flatMode,
    setLoading, setPath, setError, setFiles, filesCacheRef, inflightRef,
    kidsInflightRef, folderKidsCacheRef, resetSession, sortAscByMtime, featuresManager,
}) {

    useEffect(() => { treeRef.current = tree; }, [tree]);

    useEffect(() => {
        setTree(prev => {
            const next = { ...prev };
            if (!next['shared-with-me']) {
                next['shared-with-me'] = {
                    name: 'Відкриті для мене',
                    type: FOLDER_MIME,
                    icon: null,
                    children: null,          // важливо: null → ще не завантажено
                    loadingChildren: false,  // прапорець
                };
            }
            if (!next['shared-drives']) {
                next['shared-drives'] = {
                    name: 'Спільні диски',
                    type: FOLDER_MIME,
                    icon: null,
                    children: null,
                    loadingChildren: false,
                };
            }
            return next;
        });
    }, []);

    const features = featuresManager?.features || {
        sharedDrives: { requested: false, enabled: true },
        sharedWithMe: { requested: false, enabled: true },
    };

    // безпечне оновлення вузла
    const upsertNode = (prev, id, partial) => {
        const cur = prev[id] || {};
        return { ...prev, [id]: { ...cur, ...partial } };
    };

    // акуратно підставляє children і, за потреби, додаткові прапорці
    const attachChildren = (prev, id, kids, extra = {}) => {
        const cur = prev[id] || {};
        return {
            ...prev,
            [id]: {
                ...cur,
                name: cur.name || (id === 'shared-drives' ? 'Спільні диски' : id === 'shared-with-me' ? 'Відкриті для мене' : 'Folder'),
                children: kids,   // масив (не null) → означає "вже завантажено"
                ...extra,
            }
        };
    };

    const treeRef = useRef({});

    const initPathWith = (rootId) => {
        const rootName = tree[rootId]?.name || expanded[rootId] || 'Усі диски';
        setPath([{ id: rootId, name: rootName }]);
    };

    // --- 1) Список файлів/папок з TTL-кешем і дедупом ---
    const getListOnce = useCallback(async (fid, { force = false } = {}) => {
        // повний стоп: нічого не робимо
        if (fid === '__SHARED__' && !features.sharedWithMe.requested) return [];
        if (typeof fid === 'string' && fid.startsWith('drive:') && !features.sharedDrives.requested) return [];

        if (fid) {
            const now = Date.now();

            // TTL-кеш
            const cached = filesCacheRef.current.get(fid);
            if (!force && cached && (now - cached.ts) < CACHE_TTL_MS) {
                return cached.files;
            }

            // дедуп «в польоті»
            const inflight = inflightRef.current.get(fid);
            if (inflight && !force) return inflight;
        }

        // свіжий запит
        const p = (async () => {
            const url = !fid
                ? `/drive.php?action=home&includeSharedWithMe=${features.sharedWithMe?.requested ? 1 : 0}&includeDrives=${features.sharedDrives?.requested ? 1 : 0}`
                : `/drive.php?action=list&folderId=${encodeURIComponent(fid)}`;
            const r = await fetch(url, { credentials: 'include' });

            if (r.status === 401) {
                resetSession();
                throw new Error('Сесія недійсна. Увійдіть знову.');
            }

            const raw = await r.text();
            const ct = r.headers.get('content-type') || '';
            let data = null;
            if (ct.includes('application/json')) {
                try { data = JSON.parse(raw); } catch { data = null; }
            }
            if (!r.ok) throw new Error(data?.error || (`HTTP ${r.status}: ` + raw.slice(0, 300)));
            if (!data) throw new Error('Очікував JSON, отримав: ' + raw.slice(0, 120));
            const files = sortAscByMtime((data.files || []).map(x => {
                const isTreeFolderNode = Array.isArray(x.children); // вузол з /home із вкладеними дітьми
                const shortcut = x.shortcut ?? (x.shortcutDetails ? {
                    targetId: x.shortcutDetails.targetId ?? null,
                    targetMimeType: x.shortcutDetails.targetMimeType ?? null,
                } : null);
                const typeSafe =
                    x.type ??
                    x.mimeType ??
                    (isTreeFolderNode ? FOLDER_MIME : null) ??
                    (shortcut ? 'application/vnd.google-apps.shortcut' : null);

                const isShortcut = typeSafe === 'application/vnd.google-apps.shortcut';
                const isShortcutFolder =
                    !!(shortcut && shortcut.targetMimeType === FOLDER_MIME);
                return {
                    id: x.id,
                    name: x.name,
                    icon: x.icon ?? x.iconLink ?? null,
                    type: typeSafe,
                    mtime: x.mtime ?? x.modifiedTime ?? null,
                    url: x.url ?? x.webViewLink ?? null,
                    thumb: x.thumb ?? x.thumbnailLink ?? null,
                    shortcut,
                    isShortcut,
                    isShortcutFolder,
                };
            }));

            if (fid) filesCacheRef.current.set(fid, { ts: Date.now(), files });
            return files;
        })();

        inflightRef.current.set(fid, p);
        try { return await p; }
        finally { inflightRef.current.delete(fid); }
    }, [resetSession, sortAscByMtime, features]);

    // --- 2) Завантажити вміст поточної папки у праву панель ---
    const loadList = useCallback(async (fid, { force = false } = {}) => {
        setFolderId(fid);
        setLoading(true); setError('');
        try {
            const list = await getListOnce(fid, { force });
            setFiles(list);
        } catch (e) {
            setError(e.message || 'Помилка завантаження списку');
        } finally {
            setLoading(false);
        }
    }, [getListOnce]);

    // --- 3) Рекурсивний збирач ФАЙЛІВ (не папок) під rootId ---
    const collectAllFilesUnder = useCallback(async (rootId) => {
        const seen = new Set();
        const queue = [rootId];
        const acc = [];

        while (queue.length) {
            const fid = queue.shift();
            if (seen.has(fid)) continue;
            seen.add(fid);

            let list = [];
            try { list = await getListOnce(fid); } catch { continue; }

            // файли → у результат (з іконкою та типом)
            for (const f of list) {
                if (f.type !== FOLDER_MIME) acc.push(f);
            }
            // папки → у чергу
            for (const f of list) {
                if (f.type === FOLDER_MIME || f.isShortcutFolder) queue.push(f.id);
            }
        }
        return acc;
    }, [getListOnce]);


    // --- 4) Початкове завантаження кореня ---
    const onLoadFolder = async () => {
        initPathWith(folderId);
        if (flatMode) {
            setLoading(true); setError('');
            try { setFiles(await collectAllFilesUnder(folderId)); }
            catch (e) { setError(e.message || 'Помилка'); }
            finally { setLoading(false); }
        } else {
            await loadList(folderId);
            await ensureTreeChildren(folderId, tree[folderId]?.name || 'Root', tree[folderId]?.icon ?? null, FOLDER_MIME);
        }
    };


    // --- 5) Завантажити дітей вузла в tree (узгоджено: icon/type + дедуп/кеш) ---
    const ensureTreeChildren = useCallback(
        async (
            fid,
            fname = 'Folder',
            icon = null,
            type = FOLDER_MIME,
            isShortcut = false,
            isShortcutFolder = false
        ) => {
            // 1) оновлюємо сам вузол (не затираємо існуючих дітей)
            setTree(prev =>
                upsertNode(prev, fid, {
                    name: fname,
                    icon,
                    type,
                    isShortcut,
                    isShortcutFolder,
                    children: prev[fid]?.children ?? null,
                })
            );

            // 2) якщо діти вже є — вийти (перевіряємо за актуальним снапшотом з ref)
            const snap = treeRef.current;
            if (Array.isArray(snap?.[fid]?.children)) return;

            // 3) дедуп «в польоті»
            if (kidsInflightRef.current.has(fid)) {
                await kidsInflightRef.current.get(fid);
                return;
            }

            const pickNode = (x) => ({
                id: x.id,
                name: x.name,
                icon: x.icon ?? null,
                type: x.type ?? null,
                mtime: x.mtime ?? null,
                url: x.url ?? null,
                thumb: x.thumb ?? null,
                shortcut: x.shortcut
                    ? {
                        targetId: x.shortcut.targetId ?? null,
                        targetMimeType: x.shortcut.targetMimeType ?? null,
                    }
                    : null,
                isShortcut: !!x.isShortcut,
                isShortcutFolder:
                    !!x.isShortcutFolder ||
                    (x.type === 'application/vnd.google-apps.shortcut' &&
                        x.shortcut?.targetMimeType === 'application/vnd.google-apps.folder'),
            });

            const p = (async () => {
                try {
                    // 4) кеш
                    const cachedKids = folderKidsCacheRef.current.get(fid);
                    if (cachedKids) {
                        setTree(prev =>
                            upsertNode(prev, fid, {
                                name: fname,
                                icon,
                                type,
                                isShortcut,
                                isShortcutFolder,
                                children: cachedKids,
                            })
                        );
                        return;
                    }

                    // 5) реальний запит
                    const list = await getListOnce(fid);

                    // 6) нормалізовані «повні» діти (НЕ урізаємо поля)
                    const kids = (list || []).map(pickNode);

                    // 7) запис у кеш і дерево
                    folderKidsCacheRef.current.set(fid, kids);
                    setTree(prev =>
                        upsertNode(prev, fid, {
                            name: fname,
                            icon,
                            type,
                            isShortcut,
                            isShortcutFolder,
                            children: kids,
                        })
                    );

                    // 8) плейсхолдери для дітей: для «папкоподібних» лишаємо children=null; для файлів — []
                    setTree(prev => {
                        const next = { ...prev };
                        for (const k of kids) {
                            const isFolderLike =
                                k.type === FOLDER_MIME ||
                                k.isShortcutFolder;
                            next[k.id] = {
                                ...(next[k.id] || {}),
                                name: k.name,
                                icon: k.icon,
                                type: k.type,
                                mtime: k.mtime,
                                url: k.url,
                                thumb: k.thumb,
                                shortcut: k.shortcut,
                                isShortcut: k.isShortcut,
                                isShortcutFolder: k.isShortcutFolder,
                                children: isFolderLike ? (next[k.id]?.children ?? null) : [],
                            };
                        }
                        return next;
                    });
                } catch {
                    folderKidsCacheRef.current.set(fid, []);
                    setTree(prev =>
                        upsertNode(prev, fid, {
                            name: fname,
                            icon,
                            type,
                            isShortcut,
                            isShortcutFolder,
                            children: [],
                        })
                    );
                }
            })();

            kidsInflightRef.current.set(fid, p);
            try {
                await p;
            } finally {
                kidsInflightRef.current.delete(fid);
            }
        }, [getListOnce]);


    const loadDrives = useCallback(async () => {
        const r = await fetch('/drive.php?action=drives', { credentials: 'include' });
        if (r.status === 401) {
            resetSession();
            throw new Error('Сесія недійсна. Увійдіть знову.');
        }
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Не вдалося завантажити спільні диски');
        // очікуємо { drives: [{id,name}, ...] }
        return data?.drives || [];
    }, [resetSession]);


    // --- 6) Ліниве розгортання "Відкриті для мене"
    const expandSharedWithMe = useCallback(async () => {
        if (!features.sharedWithMe.requested) return;

        const nodeId = 'shared-with-me';

        setTree(prev => {
            const n = prev[nodeId] || { name: 'Відкриті для мене', type: FOLDER_MIME };
            return upsertNode(prev, nodeId, { loadingChildren: true, children: n.children ?? null, name: 'Відкриті для мене' });
        });

        try {
            const list = await getListOnce('__SHARED__');
            const kids = (list || []).map(x => ({
                id: x.id,
                name: x.name,
                icon: x.icon ?? null,
                type: x.type ?? null,
                mtime: x.mtime ?? null,
                url: x.url ?? null,
                thumb: x.thumb ?? null,
                shortcut: x.shortcut ?? null,
                isShortcut: !!x.isShortcut,
                isShortcutFolder: !!x.isShortcutFolder,
            }));

            folderKidsCacheRef.current.set(nodeId, kids);
            setTree(prev => attachChildren(prev, nodeId, kids, { loaded: true, expanded: true }));

            setTree(prev => {
                const next = { ...prev };
                for (const k of kids) {
                    const isFolderLike = k.type === FOLDER_MIME || k.isShortcutFolder;
                    next[k.id] = {
                        ...(next[k.id] || {}),
                        name: k.name,
                        icon: k.icon ?? null,
                        type: k.type ?? null,
                        mtime: k.mtime ?? null,
                        url: k.url ?? null,
                        thumb: k.thumb ?? null,
                        shortcut: k.shortcut ?? null,
                        isShortcut: k.isShortcut,
                        isShortcutFolder: k.isShortcutFolder,
                        children: isFolderLike ? (next[k.id]?.children ?? null) : [],
                    };
                }
                return next;
            });
        } catch (e) {
            setTree(prev => attachChildren(prev, nodeId, [], { error: e.message || 'Load failed', expanded: true }));
        } finally {
            setTree(prev => upsertNode(prev, nodeId, { loadingChildren: false }));
        }
    }, [features.sharedWithMe.requested, getListOnce, setTree, folderKidsCacheRef]);


    // --- 7) Ліниве розгортання "Спільні диски"
    const expandSharedDrives = useCallback(async () => {
        if (!features.sharedDrives.requested) return;

        const nodeId = 'shared-drives';

        setTree(prev => {
            const n = prev[nodeId] || { name: 'Спільні диски', type: FOLDER_MIME };
            return upsertNode(prev, nodeId, { loadingChildren: true, children: n.children ?? null, name: 'Спільні диски' });
        });

        try {
            const drives = await loadDrives();
            const kids = (drives || []).map(d => ({
                id: `drive:${d.id}`,
                name: d.name,
                type: FOLDER_MIME,
                icon: null,
                mtime: null,
                url: null,
                thumb: null,
                shortcut: null,
                isShortcut: false,
                isShortcutFolder: true,
            }));

            folderKidsCacheRef.current.set(nodeId, kids);
            setTree(prev => attachChildren(prev, nodeId, kids, { loaded: true, expanded: true }));

            // плейсхолдери піддисків
            setTree(prev => {
                const next = { ...prev };
                for (const k of kids) {
                    next[k.id] = {
                        ...(next[k.id] || {}),
                        name: k.name,
                        icon: k.icon,
                        type: k.type,
                        isShortcutFolder: true,
                        children: next[k.id]?.children ?? null, // ліниво підвантажимо при кліку
                    };
                }
                return next;
            });
        } catch (e) {
            setTree(prev => attachChildren(prev, nodeId, [], { error: e.message || 'Load failed', expanded: true }));
        } finally {
            setTree(prev => upsertNode(prev, nodeId, { loadingChildren: false }));
        }
    }, [features.sharedDrives.requested, loadDrives, setTree, folderKidsCacheRef]);


    // --- 8) Ліниве підвантаження дітей (виклик із UI). Тепер — обгортка над ensureTreeChildren ---
    const ensureChildren = useCallback(async (parentId) => {
        // виставляємо прапорець завантаження, НЕ змінюючи children якщо вже масив
        setTree(prev => {
            const node = prev[parentId] || { name: 'Folder', type: FOLDER_MIME };
            return upsertNode(prev, parentId, { loadingChildren: true, children: node.children ?? null });
        });

        try {
            if (features.sharedWithMe.requested && parentId === 'shared-with-me') {
                await expandSharedWithMe();
            } else if (features.sharedDrives.requested && parentId === 'shared-drives') {
                await expandSharedDrives();
            } else {
                const node = tree[parentId] || {};
                await ensureTreeChildren(
                    parentId,
                    node.name || 'Folder',
                    node.icon ?? null,
                    node.type ?? FOLDER_MIME,
                    !!node.isShortcut,
                    !!node.isShortcutFolder
                );
            }
        } finally {
            setTree(prev => upsertNode(prev, parentId, { loadingChildren: false }));
        }
    }, [expandSharedWithMe, expandSharedDrives, ensureTreeChildren, tree]);


    // --- 9)  ---
    const openSharedDrive = useCallback(async (driveId, driveName = 'Shared Drive') => {
        const fid = driveId.startsWith('drive:') ? driveId : `drive:${driveId}`;

        // breadcrumbs
        setPath(prev => {
            const i = prev.findIndex(p => p.id === fid);
            if (i >= 0) return prev.slice(0, i + 1);
            return [...prev, { id: fid, name: driveName }];
        });

        // центр (праворуч): список цього вузла
        await loadList(fid);

        // дерево: діти вузла (щоб можна було розгортати ліворуч)
        await ensureTreeChildren(fid, driveName, null, FOLDER_MIME, false, false);
    }, [setPath, loadList, ensureTreeChildren]);


    // --- 10) Розгорнути/згорнути вузол (з іконкою/типом) ---
    const toggleNode = useCallback(async (id, name, icon = null, type = FOLDER_MIME) => {
        // Мінімум метаданих для вузла
        setTree(prev => upsertNode(prev, id, { name, icon, type }));

        let willOpen = false;
        setExpanded(prev => {
            const next = !prev[id];
            willOpen = next;
            return { ...prev, [id]: next };
        });

        // Якщо ми закриваємо — просто вийти
        if (!willOpen) return;

        // Вузли-«hub’и»
        if (id === 'shared-drives') {
            if (features.sharedDrives.requested) await expandSharedDrives();
            return;
        }
        if (id === 'shared-with-me') {
            if (features.sharedWithMe.requested) await expandSharedWithMe();
            return;
        }

        // Звичайна папка / ярлик-на-папку
        await ensureChildren(id);
    }, [ensureChildren, expandSharedDrives, expandSharedWithMe, setTree, setExpanded]);


    // --- 11) Увійти в папку ---
    async function enterFolder(node) {
        if (!node) return;
        if (node.id === 'shared-drives' && !features.sharedDrives.requested) return;
        if (node.id === 'shared-with-me' && !features.sharedWithMe.requested) return;

        const id = String(node?.id || '');
        const name = node?.name || 'Folder';
        const icon = node?.icon ?? null;
        const type = node?.type ?? FOLDER_MIME;
        setError('');

        // 1) Спеціальний вузол — "Спільні диски": показуємо список дисків як папки
        if (id === 'shared-drives') {
            try {
                setLoading(true);
                const drives = await loadDrives(); // <-- масив [{id,name}]
                // breadcrumbs: лише корінь "Спільні диски"
                setPath([{ id: 'shared-drives', name: 'Спільні диски' }]);
                // у праву панель кладемо віртуальні "папки-диски"
                const folders = drives.map(d => ({
                    id: `drive:${d.id}`,           // важливо: префікс "drive:"
                    name: d.name,
                    type: FOLDER_MIME,
                    icon: null,
                    mtime: null,
                    url: null,
                    thumb: null,
                    isShortcut: false,
                    isShortcutFolder: false,
                }));
                setFiles(folders);
            } catch (e) {
                setError(e.message || 'Не вдалося завантажити спільні диски');
                setFiles([]);
            } finally {
                setLoading(false);
            }
            return; // усе зробили — вийти
        }

        // 2) Оновлюємо breadcrumbs для звичайних вузлів (у т.ч. drive:<ID>)
        setPath(prev => {
            const i = prev.findIndex(p => p.id === id);
            if (i >= 0) return prev.slice(0, i + 1);
            return [...prev, { id, name }];
        });

        // 3) Якщо це корінь конкретного Shared Drive
        if (id.startsWith('drive:')) {
            try {
                setLoading(true);
                await loadList(id); // бек розуміє 'drive:<ID>'
                // щоб ліве дерево могло розгортатися під цим диском
                await ensureTreeChildren(id, name, icon, FOLDER_MIME, false, false);
            } catch (e) {
                setError(e.message || 'Помилка завантаження диска');
            } finally {
                setLoading(false);
            }
            return;
        }

        // 4) Звичайна папка/ярлик-на-папку
        if (flatMode) {
            setLoading(true);
            try {
                const all = await collectAllFilesUnder(id);
                setFiles(all);
            } catch (e) {
                setError(e.message || 'Помилка під час рекурсивного завантаження');
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(true);
            try {
                await loadList(id);
                await ensureTreeChildren(id, name, icon, type, !!node.isShortcut, !!node.isShortcutFolder);
            } catch (e) {
                setError(e.message || 'Помилка завантаження папки');
            } finally {
                setLoading(false);
            }
        }
    }



    return {
        enterFolder, onLoadFolder, toggleNode, ensureChildren, loadList, expandSharedWithMe, loadDrives, expandSharedDrives, openSharedDrive,
    };
}
